import { cva, type VariantProps } from 'class-variance-authority';

export const toggleGroupRootVariants = cva(
  [
    'inline-flex items-center gap-xxs',
    'data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
  ],
  {
    variants: {
      wrap: {
        true: 'flex-wrap',
        false: 'flex-nowrap',
      },
    },
    defaultVariants: {
      wrap: true,
    },
  },
);

/**
 * Mirrors button-like visuals from `button.variants.ts` but uses `data-[state=on]:*`
 * for selection. Radix ToggleGroup items expose `data-state` in both modes; in
 * single-select, items use `role="radio"` and `aria-checked`, not `aria-pressed`,
 * so Button’s `aria-pressed:*` utilities would not apply to the selected item.
 */
export const toggleGroupItemVariants = cva(
  [
    'inline-flex items-center justify-center',
    'rounded-max border-2 font-body font-bold',
    'transition-colors duration-normal',
    'focus-visible:outline-none focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-offset-focus focus-visible:ring-offset-1',
    'disabled:pointer-events-none disabled:opacity-40',
  ],
  {
    variants: {
      variant: {
        blackFill: [
          'border-black bg-white text-black',
          'hover:bg-neutral-50 active:bg-neutral-100',
          'data-[state=on]:bg-black data-[state=on]:text-white data-[state=on]:hover:bg-neutral-500 data-[state=on]:active:bg-neutral-700',
        ],
        grayOutline: [
          'border-neutral-200 bg-white text-black',
          'hover:bg-neutral-50 active:bg-neutral-100',
          'data-[state=on]:border-neutral-500 data-[state=on]:bg-neutral-100',
        ],
        // Filter-pill treatment: secondary-tinted outline when off,
        // filled secondary when on (selected). Used by the offerings EL
        // tag-filter bar.
        secondary: [
          'border-neutral-200 bg-white text-secondary-700',
          'hover:bg-secondary-50 active:bg-secondary-100',
          'data-[state=on]:border-secondary-700 data-[state=on]:bg-secondary-700 data-[state=on]:text-white',
        ],
      },
      size: {
        // Compact filter-pill size: 10px label, responsive xs side
        // padding (8px mobile / 12px desktop) + min vertical padding.
        xs: 'px-xs py-min text-l2 leading-l2',
        sm: 'px-sm py-l1 text-b5 leading-b5',
        lg: 'px-lg py-l1 text-b4 leading-b4',
      },
    },
    defaultVariants: {
      variant: 'grayOutline',
      size: 'sm',
    },
  },
);

export type ToggleGroupRootVariantProps = VariantProps<
  typeof toggleGroupRootVariants
>;
export type ToggleGroupItemVariantProps = VariantProps<
  typeof toggleGroupItemVariants
>;
